import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { onMustChangePassword, onSessionEnd, refreshSession } from '../api/client'
import { authApi, type Me, type ProfileInput } from './api'
import { can as canFn, type PermissionCheck } from './permissions'
import { tokens } from './tokens'

type Status = 'loading' | 'anonymous' | 'authenticated'

type AuthState = {
  status: Status
  user: Me | null
  permissions: string[]
  endedReason: string | null
  can: (p: PermissionCheck) => boolean
  /** OTP gerekiyorsa ön doğrulama anahtarını, güvenilen cihazda doğrudan oturum açıldıysa null döner. */
  startLogin: (email: string, password: string) => Promise<string | null>
  /** remember: cihaz 30 gün güvenilir sayılır (sonraki girişlerde yalnız şifre sorulur). */
  completeLogin: (preAuthToken: string, otp: string, email: string, remember: boolean) => Promise<void>
  reloadMe: () => Promise<void>
  updateProfile: (input: ProfileInput) => Promise<void>
  /** Parola değişimi 1. adım: kod gönderir, ön doğrulama anahtarını döner. */
  requestPasswordOtp: (current: string, next: string) => Promise<string>
  /** 2. adım: kodla yeni parolayı kaydeder; sunucu tüm oturumları kapattığı için yerel oturum da kapanır. */
  changePassword: (preAuthToken: string, otp: string, next: string) => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

async function ensureCsrf() {
  if (!tokens.csrf) await authApi.csrf().catch(() => undefined)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [status, setStatus] = useState<Status>('loading')
  const [user, setUser] = useState<Me | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [endedReason, setEndedReason] = useState<string | null>(null)

  const loadMe = useCallback(async () => {
    const me = await authApi.me()
    tokens.setUserId(me.id)
    setUser(me)
    setPermissions(me.permissions ?? [])
    setStatus('authenticated')
  }, [])

  const logout = useCallback(async () => {
    const rt = tokens.refresh
    try {
      if (rt) await authApi.logout(rt)
    } catch {
      /* oturum sunucuda zaten kapanmış olabilir; yerel çıkış yine yapılır */
    } finally {
      tokens.clear()
      qc.clear()
      setUser(null)
      setPermissions([])
      setStatus('anonymous')
    }
  }, [qc])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (tokens.refresh && (await refreshSession())) {
        try {
          await ensureCsrf()
          await loadMe()
          return
        } catch {
          /* anonim */
        }
      }
      if (!cancelled) setStatus('anonymous')
    })()
    return () => {
      cancelled = true
    }
  }, [loadMe])

  useEffect(
    () =>
      tokens.onAccessChange((access) => {
        if (!access) return
        void loadMe().catch(() => undefined)
      }),
    [loadMe],
  )

  // Oturum sekmeler arasında paylaşılır: başka sekmede çıkış yapılınca bu sekme de kapanır, giriş yapılınca açılır.
  useEffect(
    () =>
      tokens.onOtherTab((event) => {
        if (event === 'out') {
          setUser(null)
          setPermissions([])
          setEndedReason(null)
          setStatus('anonymous')
          qc.clear()
          return
        }
        void (async () => {
          if (await refreshSession()) {
            await ensureCsrf()
            await loadMe().catch(() => undefined)
          }
        })()
      }),
    [loadMe, qc],
  )

  useEffect(
    () =>
      onMustChangePassword(() => {
        setUser((current) => (current ? { ...current, must_change_password: true } : current))
      }),
    [],
  )

  useEffect(
    () =>
      onSessionEnd((reason) => {
        tokens.setUserId(null)
        setUser(null)
        setPermissions([])
        setEndedReason(reason)
        setStatus('anonymous')
        qc.clear()
      }),
    [qc],
  )

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      permissions,
      endedReason,
      can: (p) => canFn(permissions, p),
      startLogin: async (email, password) => {
        const device = tokens.deviceToken(email)
        const res = await authApi.login(email, password, device)
        if (res.session) {
          tokens.set(res.session)
          setEndedReason(null)
          await ensureCsrf()
          await loadMe()
          return null
        }
        // Cihaz belirteci kabul edilmediyse (süresi doldu ya da iptal edildi) bir daha gönderilmez.
        if (device) tokens.setDeviceToken(email, null)
        return res.pre_auth_token ?? ''
      },
      completeLogin: async (pre, otp, email, remember) => {
        const pair = await authApi.verifyOtp(pre, otp, remember)
        if (pair.device_token) tokens.setDeviceToken(email, pair.device_token)
        tokens.set(pair)
        setEndedReason(null)
        await ensureCsrf()
        await loadMe()
      },
      reloadMe: loadMe,
      updateProfile: async (input) => {
        await authApi.updateProfile(input)
        await loadMe()
      },
      requestPasswordOtp: async (current, next) => (await authApi.requestPasswordOtp(current, next)).pre_auth_token,
      changePassword: async (pre, otp, next) => {
        await authApi.changePassword(pre, otp, next)
        // Sunucu şifre değişiminde güvenilen cihazları iptal eder; yerel belirteç de silinir.
        if (user?.email) tokens.setDeviceToken(user.email, null)
        tokens.clear()
        qc.clear()
        setUser(null)
        setPermissions([])
        setStatus('anonymous')
      },
      logout,
    }),
    [status, user, permissions, endedReason, loadMe, qc, logout],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth AuthProvider dışında kullanıldı')
  return v
}
